import ExistingInstances from './ExistingInstances.jsx'
import './App.css';
import { PageLayout } from "./PageLayout";
// import NewInstance from './NewInstance.jsx'; UNCOMMENT FOR NEW INSTANCE SUPPORT
import { Row, Stack } from 'react-bootstrap';
import { useGoogleLogin } from '@react-oauth/google';
import React from 'react';

const apiUrl = process.env.REACT_APP_API_URL

// Cookie utility functions
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax;Secure`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

function App() {
  const [user, setUser] = React.useState(null);
  const [profile, setProfile] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [adminMode, setAdminMode] = React.useState(false);

  const checkAdminStatus = async (email) => {
    try {
      const res = await fetch('/admins.json');
      if (res.ok) {
        const data = await res.json();
        const adminList = data.admins || [];
        setIsAdmin(adminList.includes(email));
      }
    } catch (err) {
      console.error("Failed to load admin list:", err);
    }
  };

  // Check for existing auth on component mount
  React.useEffect(() => {
    const checkExistingAuth = async () => {
      const savedToken = getCookie('google_access_token');
      const savedProfile = getCookie('user_profile');

      if (savedToken && savedProfile) {
        try {
          // Verify token is still valid
          const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
            headers: {
              Authorization: `Bearer ${savedToken}`,
            },
          });

          if (res.ok) {
            const profileData = JSON.parse(decodeURIComponent(savedProfile));
            setUser({ access_token: savedToken });
            setProfile(profileData);
            await checkAdminStatus(profileData.email);
          } else {
            // Token expired, clear cookies
            deleteCookie('google_access_token');
            deleteCookie('user_profile');
          }
        } catch (err) {
          console.error("Failed to verify existing token:", err);
          deleteCookie('google_access_token');
          deleteCookie('user_profile');
        }
      }
      setIsLoading(false);
    };

    checkExistingAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useGoogleLogin({
    prompt: "select_account", 
    onSuccess: async (tokenResponse) => {
      try {
        setUser(tokenResponse);

        const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        });

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const profileData = await res.json();
        setProfile(profileData);
        await checkAdminStatus(profileData.email);

        // Save to cookies (30 days expiration)
        setCookie('google_access_token', tokenResponse.access_token, 30);
        setCookie('user_profile', encodeURIComponent(JSON.stringify(profileData)), 30);
        
      } catch (err) {
        console.error("Failed to fetch user profile:", err);
      }
    },
    onError: () => alert("Login Failed"),
  });

  const handleLogout = () => {
    deleteCookie('google_access_token');
    deleteCookie('user_profile');
    setProfile(null);
    setUser(null);
    setIsAdmin(false);
    setAdminMode(false);
  };

  if (isLoading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <PageLayout
        login={login}
        user={user}
        profile={profile}
        setProfile={setProfile}
        setUser={setUser}
        onLogout={handleLogout}
        isAdmin={isAdmin}
        adminMode={adminMode}
        setAdminMode={setAdminMode}
      >
        {user ? (
          <Stack gap={3}>
            {/* <Row><NewInstance /></Row> */}
            <Row>
              <ExistingInstances
                apiUrl={apiUrl}
                userProfile={profile}
                accessToken={user?.access_token}
                onAuthError={handleLogout}
                isAdmin={isAdmin}
                adminMode={adminMode}
              />
            </Row>
          </Stack>
        ) : (
          <div className="login-page">
            <div className="login-content">
              <h1 className="login-title">Welcome to The Heist Portal</h1>
              <p className="login-subtitle">
                Please use your Google credentials to access AWS instances.
              </p>
              <div className="login-button-container">
                <button 
                  className="google-login-button"
                  onClick={() => login()}
                >
                  <svg className="google-icon" width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </button>
              </div>
            </div>
          </div>
        )}
      </PageLayout>
    </div>
  );
}

export default App;